import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ReservationForm } from '../components/ReservationForm';
import { useLanguage } from '../context/LanguageContext';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  FileText,
  MessageCircle,
  Instagram,
  Facebook
} from 'lucide-react';

export const ContactPage = () => {
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    if (location.hash === '#reservar') {
      const element = document.getElementById('reservar');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  const contactInfo = [
    {
      icon: Phone,
      title: t('contact.info.phone.title'),
      value: '+52 (55) 1234-5678',
      description: t('contact.info.phone.description'),
      action: 'tel:+525512345678',
    },
    {
      icon: MessageCircle,
      title: t('contact.info.whatsapp.title'),
      value: '+52 (55) 8765-4321',
      description: t('contact.info.whatsapp.description'),
      action: 'https://wa.me/5558765432',
    },
    {
      icon: Mail,
      title: t('contact.info.email.title'),
      value: 'info@fastcar.mx',
      description: t('contact.info.email.description'),
      action: 'mailto:info@fastcar.mx',
    },
    {
      icon: MapPin,
      title: t('contact.info.location.title'),
      value: 'Ciudad de México',
      description: t('contact.info.location.description'),
      action: null,
    },
  ];

  const hours = [
    { day: t('contact.hours.weekdays'), hours: t('contact.hours.allDay') },
    { day: t('contact.hours.saturday'), hours: t('contact.hours.allDay') },
    { day: t('contact.hours.sunday'), hours: t('contact.hours.allDay') },
    { day: t('contact.hours.holidays'), hours: t('contact.hours.allDay') },
  ];

  const socialLinks = [
    { icon: Instagram, name: 'Instagram', url: '#' },
    { icon: Facebook, name: 'Facebook', url: '#' },
    { icon: MessageCircle, name: 'WhatsApp', url: 'https://wa.me/5558765432' },
  ];

  const faqQuestions = t('contact.faq.questions');

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 noise-overlay" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="text-sm tracking-widest uppercase text-sky-400 font-medium mb-4 block">
              {t('contact.sectionLabel')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {t('contact.title')}{' '}
              <span className="text-gradient">{t('contact.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              {t('contact.subtitle')}
            </p>
            <div className="badge-billing bg-emerald-900/30 border-emerald-700 text-emerald-400">
              <FileText className="w-4 h-4" />
              <span>{t('contact.badge')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <a
                key={index}
                href={info.action}
                data-testid={`contact-card-${index}`}
                className={`card-service text-center group ${info.action ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <div className="w-14 h-14 bg-sky-100 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-sky-500 transition-colors duration-300">
                  <info.icon className="w-7 h-7 text-sky-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">{info.title}</h3>
                <p className="text-sky-600 font-medium mb-1">{info.value}</p>
                <p className="text-slate-500 text-sm">{info.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Reservation Form Section */}
      <section id="reservar" className="section-padding bg-white scroll-mt-24">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Form */}
            <div data-testid="reservation-section">
              <span className="text-sm tracking-widest uppercase text-sky-600 font-medium mb-4 block">
                {t('form.title')}
              </span>
              <h2 className="section-title mb-6">
                {t('form.title')}
              </h2>
              <p className="text-slate-500 leading-relaxed mb-8">
                {t('form.subtitle')}
              </p>
              <ReservationForm />
            </div>

            {/* Right - Additional Info */}
            <div className="space-y-8">
              {/* Hours */}
              <div className="card-service">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-sky-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">{t('contact.hours.title')}</h3>
                </div>
                <div className="space-y-3">
                  {hours.map((item, index) => (
                    <div 
                      key={index}
                      className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0"
                    >
                      <span className="text-slate-600">{item.day}</span>
                      <span className="font-medium text-slate-900">{item.hours}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Billing Info */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">{t('contact.billing.title')}</h3>
                </div>
                <p className="text-slate-600 leading-relaxed mb-4">
                  {t('contact.billing.description')}
                </p>
                <ul className="space-y-2">
                  {t('contact.billing.features').map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-slate-700">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Social Links */}
              <div className="card-service">
                <h3 className="text-xl font-bold text-slate-900 mb-6">{t('contact.social')}</h3>
                <div className="flex gap-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      data-testid={`social-link-${index}`}
                      className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center hover:bg-sky-500 hover:text-white transition-colors group"
                    >
                      <social.icon className="w-6 h-6 text-slate-600 group-hover:text-white transition-colors" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="rounded-2xl overflow-hidden shadow-lg h-64 bg-slate-200">
                <iframe
                  title="Ubicación FastCar"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d240861.14426893026!2d-99.42379359999999!3d19.390519!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x85ce0026db097507%3A0x54061076265ee841!2sCiudad%20de%20M%C3%A9xico%2C%20CDMX!5e0!3m2!1ses!2smx!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-sm tracking-widest uppercase text-sky-600 font-medium mb-4 block">
              {t('contact.faq.sectionLabel')}
            </span>
            <h2 className="section-title mb-4">
              {t('contact.faq.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {faqQuestions.map((faq, index) => (
              <div 
                key={index}
                data-testid={`faq-${index}`}
                className="card-service"
              >
                <h4 className="font-bold text-slate-900 mb-2">{faq.q}</h4>
                <p className="text-slate-500">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
